// The drawer's pad in VR: a 60 × 40 cm drawing surface that appears in front
// of you (chest height) when your turn starts and stays put, mirroring the
// big easel 1:1 — what you draw here is what everyone sees on the easel.
//
// Hands (the competition rule — fully playable without controllers): draw by
// touching the pad with your index fingertip, like a whiteboard. A ring shows
// where your finger is and turns solid when the pen is down; pull your finger
// back to lift the pen. No pinch is needed (pinch is teleport / click).
// Controllers: point at the pad (or the easel) and hold the trigger.
//
// Big poke targets under the pad: 8 colours, 3 brush sizes, Undo, Clear, and
// "Move pad" (brings it back in front of you).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import { hands, type HandSide } from '../../VR/hands/handTracking';
import { useVRBuild } from '../../VR/build/vrBuildState';
import { VRBtn, Surface, noRaycast, vrColors as colors } from '../kit/vrKit';
import { useCountdown } from '../kit/LobbyKit';
import { COLORS, SIZES, SIZE_NAMES, penFor, usePenStyle, useStrokeCount } from './strokes';
import { useBoardTexture, usePlanePen } from './DrawingTable3D';

export const PAD_W = 0.6;
export const PAD_H = 0.4;
const PANEL_W = 0.72, PANEL_H = 0.7;
const AREA_Y = 0.045;
// Fingertip pen: down within this band around the surface (m, + = in front).
const DOWN_FRONT = 0.012, DOWN_BACK = -0.06;
const UP_FRONT = 0.03, UP_BACK = -0.1, EDGE = 0.04, HOVER = 0.15;

const _w = new THREE.Vector3();
const _l = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _fwd = new THREE.Vector3();
const _obj = new THREE.Object3D();
const _v2 = new THREE.Vector2();

function Swatch({ color, x, y, on, onPress }: { color: string; x: number; y: number; on: boolean; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <group position={[x, y, 0.006]} name={`vr-draw-color-${color}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); onPress(); }}>
      {(on || hover) && <Surface width={0.07} height={0.07} color={on ? colors.teal : colors.hover} r={0.012} z={-0.001} />}
      <Surface width={0.058} height={0.058} color={color} r={0.01} />
    </group>
  );
}

export function VRDrawPad({ worldId, tableId, word, deadline, children }: {
  worldId: string; tableId: string; word: string | null; deadline: number | null; children?: React.ReactNode;
}) {
  const root = useRef<THREE.Group>(null);
  const area = useRef<THREE.Mesh>(null);
  const cursor = useRef<THREE.Mesh>(null);
  const cursorMat = useRef<THREE.MeshBasicMaterial>(null);
  const { camera } = useThree();
  const origin = useXR((s) => s.origin);
  const anchor = useRef<{ pos: THREE.Vector3; quat: THREE.Quaternion } | null>(null);
  const penHand = useRef<HandSide | null>(null);
  const smooth = useRef(new THREE.Vector2());
  const { texture } = useBoardTexture(tableId, 768, 512);
  const pen = penFor(worldId, tableId);
  const { color, size, setColor, setSize } = usePenStyle();
  const empty = useStrokeCount(tableId) === 0;
  const left = useCountdown(deadline);
  // Controllers: trigger on the pad.
  const { mesh: padMesh, handlers } = usePlanePen(worldId, tableId, true, PAD_W, PAD_H);
  const setArea = (m: THREE.Mesh | null) => { (area as any).current = m; (padMesh as any).current = m; };

  // Lift the pen (and give the pinch back to teleport) if the pad goes away mid-stroke.
  useEffect(() => () => {
    if (penHand.current) { pen.up(); penHand.current = null; }
    useVRBuild.getState().setHandling(false);
  }, [pen]);

  const place = () => {
    camera.getWorldPosition(_w);
    camera.getWorldDirection(_fwd); _fwd.y = 0;
    if (_fwd.lengthSq() < 1e-6) _fwd.set(0, 0, -1);
    _fwd.normalize();
    const pos = _w.clone().addScaledVector(_fwd, 0.42);
    pos.y = _w.y - 0.3;
    _obj.position.copy(pos);
    _obj.lookAt(_w);
    anchor.current = { pos, quat: _obj.quaternion.clone() };
  };

  useFrame(() => {
    const g = root.current;
    if (!g?.parent) return;
    if (!anchor.current) place();
    const a = anchor.current!;
    g.position.copy(g.parent.worldToLocal(_l.copy(a.pos)));
    g.parent.getWorldQuaternion(_q);
    g.quaternion.copy(_q.invert().multiply(a.quat));
    g.updateMatrixWorld();

    // ---- fingertip pen ----
    const m = area.current, c = cursor.current;
    if (!m || !c) return;
    let best: { side: HandSide; x: number; y: number; z: number } | null = null;
    if (origin) {
      origin.updateMatrixWorld();
      for (const side of ['right', 'left'] as HandSide[]) {
        const h = hands[side];
        if (!h.present) continue;
        if (penHand.current && penHand.current !== side) continue;
        m.worldToLocal(_l.copy(h.indexTip).applyMatrix4(origin.matrixWorld));
        const inside = Math.abs(_l.x) <= PAD_W / 2 + 0.01 && Math.abs(_l.y) <= PAD_H / 2 + 0.01;
        if (!inside && !penHand.current) continue;
        if (!best || Math.abs(_l.z) < Math.abs(best.z)) best = { side, x: _l.x, y: _l.y, z: _l.z };
      }
    }
    const down = penHand.current !== null;
    if (best) {
      const nx = THREE.MathUtils.clamp(best.x / PAD_W + 0.5, 0, 1), ny = THREE.MathUtils.clamp(0.5 - best.y / PAD_H, 0, 1);
      if (!down) {
        if (best.z < DOWN_FRONT && best.z > DOWN_BACK) {
          penHand.current = best.side;
          smooth.current.set(nx, ny);
          useVRBuild.getState().setHandling(true);
          pen.down(nx, ny);
        }
      } else {
        const off = best.z > UP_FRONT || best.z < UP_BACK || Math.abs(best.x) > PAD_W / 2 + EDGE || Math.abs(best.y) > PAD_H / 2 + EDGE;
        if (off) {
          penHand.current = null; pen.up();
          useVRBuild.getState().setHandling(false);
        } else {
          // Light smoothing against tracking jitter.
          smooth.current.lerp(_v2.set(nx, ny), 0.55);
          pen.move(smooth.current.x, smooth.current.y);
        }
      }
      const show = best.z < HOVER && best.z > UP_BACK;
      c.visible = show;
      if (show) {
        c.position.set(THREE.MathUtils.clamp(best.x, -PAD_W / 2, PAD_W / 2), THREE.MathUtils.clamp(best.y, -PAD_H / 2, PAD_H / 2), 0.003);
        const r = Math.max(0.006, (SIZES[usePenStyle.getState().size] * PAD_W) / 2);
        const near = 1 - THREE.MathUtils.clamp(best.z / HOVER, 0, 1);
        c.scale.setScalar(r * (penHand.current ? 1 : 1.6 - 0.6 * near));
        if (cursorMat.current) {
          cursorMat.current.color.set(COLORS[usePenStyle.getState().color]);
          cursorMat.current.opacity = penHand.current ? 1 : 0.35 + 0.5 * near;
        }
      }
    } else {
      c.visible = false;
      if (down) { penHand.current = null; pen.up(); useVRBuild.getState().setHandling(false); }
    }
  });

  const ringGeo = useMemo(() => new THREE.RingGeometry(0.7, 1, 24), []);
  const top = PANEL_H / 2;
  return (
    <group ref={root} name="vr-draw-pad">
      <Surface width={PANEL_W} height={PANEL_H} color={colors.panel} z={-0.004} />
      <Text raycast={noRaycast} position={[-PANEL_W / 2 + 0.025, top - 0.04, 0.002]} anchorX="left" fontSize={0.026} color={colors.teal}>
        {word ? `Draw: ${word}` : 'Draw!'}
      </Text>
      {left !== null && (
        <Text raycast={noRaycast} position={[PANEL_W / 2 - 0.025, top - 0.04, 0.002]} anchorX="right" fontSize={0.024} color={left <= 10 ? '#ff9b8a' : colors.text}>
          {`${left}s`}
        </Text>
      )}
      <Text raycast={noRaycast} position={[0, top - 0.075, 0.002]} fontSize={0.0125} color={colors.muted}>
        Touch the pad with your fingertip to draw · controllers: hold the trigger
      </Text>

      {/* Drawing surface */}
      <group position={[0, AREA_Y, 0]}>
        <mesh ref={setArea} name="vr-draw-surface" {...handlers}>
          <planeGeometry args={[PAD_W, PAD_H]} />
          <meshBasicMaterial map={texture} toneMapped={false} />
        </mesh>
        <mesh ref={cursor} geometry={ringGeo} raycast={noRaycast} visible={false}>
          <meshBasicMaterial ref={cursorMat} transparent depthTest={false} toneMapped={false} />
        </mesh>
      </group>

      {/* Palette */}
      {COLORS.map((c, i) => (
        <Swatch key={c} color={c} x={-0.2625 + i * 0.075} y={-0.215} on={color === i} onPress={() => setColor(i)} />
      ))}
      {SIZES.map((_, i) => (
        <VRBtn key={i} name={`vr-draw-size-${i}`} label={SIZE_NAMES[i]} x={-0.27 + i * 0.09} y={-0.3} w={0.084} h={0.06} fontSize={0.014}
          active={size === i} onPress={() => setSize(i)} />
      ))}
      <VRBtn name="vr-draw-undo" label="Undo" x={0.03} y={-0.3} w={0.1} h={0.06} disabled={empty} onPress={() => pen.undo()} />
      <VRBtn name="vr-draw-clear" label="Clear" x={0.14} y={-0.3} w={0.1} h={0.06} disabled={empty} onPress={() => pen.clear()} />
      <VRBtn name="vr-draw-move" label="Move pad" x={0.27} y={-0.3} w={0.13} h={0.06} fontSize={0.014} onPress={place} />
      {children}
    </group>
  );
}

export default VRDrawPad;
